"use client"
import {
    CHROME_EXTENSION_STORE_URL,
    DOC_RAB_URL,
    FIREFOX_RECALL_EXTENSION_STORE_URL,
    ROUTES,
} from "@/constants/routes"
import { Box, Container, SxProps, Theme } from "@mui/material"
import Typography from "@mui/material/Typography"
import { CONTACT_US_EMAIL } from "@recall/common"
import { Fragment } from "react"
import { BottomCTA } from "../BottomCTA"
import { PageHeader } from "../layout/PageHeader"
import { ExternalLink } from "../links/ExternalLink"
import { InlineLink } from "../links/InlineLink"
import { Question } from "../Question"
import { Title, TitleSecondary } from "../typography/Title"
import SlideUpAnimation from "../utility/SlideUpAnimation"
import { jsonLd } from "./PricingFAQJsonLd"

const faqQuestions = [
    {
        question: "Do you offer refunds?",
        answer: (
            <Typography>
                Yes, if you are not satisfied with our product within the first 30 days, we will
                refund you.
            </Typography>
        ),
    },
    {
        question: "Student Discounts?",
        answer: (
            <Typography>
                Yes, please email us at{" "}
                <InlineLink href={`mailto:${CONTACT_US_EMAIL}`} color="text.secondary">
                    {CONTACT_US_EMAIL}
                </InlineLink>{" "}
                from your student email address for a 20% student discount.
            </Typography>
        ),
    },
    {
        question: "What is Augmented Browsing?",
        answer: (
            <Box>
                <Typography>
                    Augmented Browsing is a feature in Recall that enhances your web experience by
                    surfacing relevant insights from your personal knowledge base in real time. As
                    you browse, Recall automatically detects keywords and concepts on the page and
                    shows connections to content you&apos;ve previously saved—helping you instantly
                    recall past knowledge without interrupting your workflow.
                </Typography>
                <br />
                <Typography>
                    Since Augmented Browsing is local-first, all processing happens on your device,
                    ensuring privacy and security while delivering meaningful connections to what
                    you already know.
                </Typography>
                <br />
                <ExternalLink target="_blank" href={DOC_RAB_URL}>
                    Learn More about Augmented Browsing
                </ExternalLink>
            </Box>
        ),
    },
    {
        question: "What are AI summaries?",
        answer: (
            <Typography>
                AI Summaries are generated using a mix of fine-tuned AI models and proprietary
                methods to generate high-quality concise or detailed summaries. The best way to
                create summaries is with our browser extensions on{" "}
                <InlineLink
                    target="_blank"
                    href={CHROME_EXTENSION_STORE_URL}
                    color="text.secondary"
                >
                    Chrome
                </InlineLink>{" "}
                or{" "}
                <InlineLink
                    target="_blank"
                    href={FIREFOX_RECALL_EXTENSION_STORE_URL}
                    color="text.secondary"
                >
                    Firefox
                </InlineLink>
                .
            </Typography>
        ),
    },
    {
        question: "What content can you summarize?",
        answer: (
            <Typography component="div">
                Recall works best on:
                <ol>
                    <li>YouTube Videos - we can summarize up to 10 hour long YouTube videos</li>
                    <li>PDFs - we can summarize up to 300 pages</li>
                    <li>Articles</li>
                    <li>Google Docs</li>
                    <li>Blogs</li>
                    <li>Recipes</li>
                </ol>
                Please note: we are quickly expanding the content we support - if you have a request
                please add it to our{" "}
                <InlineLink target="_blank" href={ROUTES.ROADMAP} color="text.secondary">
                    roadmap
                </InlineLink>
                .
            </Typography>
        ),
    },
    {
        question: "How do I cancel, change plans, or change credit cards?",
        answer: (
            <Typography>
                You can manage your subscription from the Manage Subscription" section found in the
                main app menu.
            </Typography>
        ),
    },

    {
        question: "What are unlimited personal notes?",
        answer: (
            <Typography>
                You can use Recall as you would any other note-taking system. Simply create an
                unlimited amount of your own notes in Recall. Select an <b>empty note</b> from the
                menu in the Recall app. <br />
                NB: You can also generate quiz questions on your personal notes!
            </Typography>
        ),
    },
    {
        question: "What are AI quiz questions?",
        answer: (
            <Typography>
                For all the cards you save into Recall, you can generate a quiz! This is part of our
                goal to enable long-term retention of content you care about. You can also fully
                customize these questions - adding more manually or changing the answers.
            </Typography>
        ),
    },
    {
        question: "What is Automatic categorization?",
        answer: (
            <Typography>
                All AI summaries that you save into Recall have an automatic tag added to it which
                categorizes the card in the left panel of the application. This category is unique
                to you and existing tags you have.
            </Typography>
        ),
    },
    {
        question: "What is Automatic knowledge graph creation?",
        answer: (
            <Typography>
                This is where the magic really happens! All the AI summaries that you save into
                Recall have keywords extracted and linked, enabling you to form connections to
                related content that you otherwise would have missed. Learn more about the origins
                of graph databases in our blog.
            </Typography>
        ),
    },
    {
        question: "What language do you support?",
        answer: (
            <Typography>
                You can translate AI summaries and your quiz questions to any of the following
                languages: <br />
                🇬🇧 English <br />
                🇩🇪 German <br />
                🇫🇷 French <br />
                🇪🇸 Spanish <br />
                🇮🇹 Italian <br />
                🇨🇳 Chinese <br />
                🇯🇵 Japanese <br />
                🇰🇷 Korean <br />
                🇵🇹 Portuguese <br />
                🇷🇺 Russian
            </Typography>
        ),
    },
    {
        question: 'What does "Unlimited" mean in Recall\'s pricing?',
        answer: (
            <Typography>
                Recall&apos;s <b>Unlimited plan</b> allows you to save unlimited notes and
                summaries. However, we reserve the right to pause, review, or adjust your
                subscription if we determine that your usage significantly exceeds reasonable
                levels. Please see our{" "}
                <InlineLink href={ROUTES.FAIR_USE_POLICY} color="text.secondary">
                    Fair Use Policy
                </InlineLink>{" "}
                for details.
            </Typography>
        ),
    },
    {
        question: "Where can I find the full Terms of Service and Fair Use Policy?",
        answer: (
            <Typography>
                You can read our full{" "}
                <InlineLink href={ROUTES.TERMS_OF_SERVICE} color="text.secondary">
                    Terms of Service
                </InlineLink>{" "}
                and{" "}
                <InlineLink href={ROUTES.FAIR_USE_POLICY} color="text.secondary">
                    Fair Use Policy
                </InlineLink>{" "}
                for details on usage, payments, and service policies.
            </Typography>
        ),
    },
]

export const PricingFAQ = () => {
    return (
        <Container component="section" sx={styles.container}>
            <script
                key="structured-data"
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(jsonLd),
                }}
            />
            <PageHeader sx={styles.pageHeader}>
                <SlideUpAnimation>
                    <Title sx={styles.title}>Frequently asked questions</Title>
                </SlideUpAnimation>
                <SlideUpAnimation delay="0.2s">
                    <TitleSecondary sx={styles.titleSecondary} textAlign="center">
                        You&apos;ve got questions. We&apos;ve got answers
                    </TitleSecondary>
                </SlideUpAnimation>
            </PageHeader>
            <SlideUpAnimation delay="0.4s">
                <Box display="flex" justifyContent="center" alignItems="center">
                    <Box sx={styles.sectionContainer}>
                        {faqQuestions.map((faq, index) => (
                            <Fragment key={`quesion-${index}`}>
                                <Question id={`question-${index}`} question={faq.question}>
                                    {faq.answer}
                                </Question>
                            </Fragment>
                        ))}
                    </Box>
                </Box>
            </SlideUpAnimation>
            <SlideUpAnimation delay="0.4s">
                <BottomCTA />
            </SlideUpAnimation>
        </Container>
    )
}

const styles: Record<string, SxProps<Theme>> = {
    pageHeader: {
        mb: 14,
        py: 0,
    },
    container: {
        px: "0px !important",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        pb: 6,
    },
    title: {
        pt: 0,
        fontSize: "50px",
        lineHeight: "normal",
        pb: 5,
        color: (theme) => theme.palette.text.primary,
    },
    titleSecondary: {
        fontSize: "20px",
        fontWeight: 400,
    },
    sectionContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "90%",
        mb: 25,
    },
}
